/**
 * QuickEmailProvider — mounts a single QuickEmailModal at the app root
 * and exposes `openQuickEmail({ ... })` via the useQuickEmail() hook.
 *
 * One modal, many call sites. Consumers call:
 *
 *   const { openQuickEmail } = useQuickEmail()
 *   openQuickEmail({
 *     site: 'lead_reply',
 *     recipients: [{ name: 'Emma Chen', email: 'emma@example.com' }],
 *     context: { lead, profile },
 *     contextLabel: 'Emma Chen & Jordan Park',
 *     contextMeta: 'Lead · no prior correspondence',
 *     triggeredFrom: 'Lead card · reply',
 *     leadId: lead.id,
 *     eventId: null,
 *     eventName: null,
 *   })
 *
 * The provider pulls the planner's auth identity from AuthContext so
 * callers don't have to thread user_id through every component.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthContext } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import { QuickEmailModal } from './QuickEmailModal'
import { QuickEmailContext } from './context'

export function QuickEmailProvider({ children, onToast }) {
  const { user, profile } = useAuthContext() || {}
  const [state, setState] = useState({ open: false, config: null })

  // Built-in toast so callers don't have to wire one; can be overridden
  // by passing `onToast` to the provider.
  const [toast, setToast] = useState(null)
  const toastTimerRef = useRef(null)
  const showToast = useCallback((msg) => {
    if (!msg) return
    setToast(msg)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 2400)
  }, [])
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current) }, [])

  const openQuickEmail = useCallback((config) => {
    if (!config) return
    // Merge planner profile into context so templates resolve
    // {planner_*} / {studio_name} automatically.
    const merged = {
      ...config,
      context: {
        ...(config.context || {}),
        // Only fill profile if caller didn't supply one explicitly.
        profile: config.context?.profile ?? profile ?? null,
      },
    }
    setState({ open: true, config: merged })
  }, [profile])

  const closeQuickEmail = useCallback(() => {
    setState(s => ({ ...s, open: false }))
  }, [])

  const value = useMemo(() => ({ openQuickEmail, closeQuickEmail }), [openQuickEmail, closeQuickEmail])

  const cfg = state.config || {}
  const effectiveToast = onToast || cfg.onToast || showToast

  return (
    <QuickEmailContext.Provider value={value}>
      {children}
      <QuickEmailModal
        open={state.open}
        onClose={closeQuickEmail}
        site={cfg.site}
        recipients={cfg.recipients || []}
        cc={cfg.cc || []}
        bcc={cfg.bcc || []}
        bulkMode={cfg.bulkMode || false}
        context={cfg.context || {}}
        contextLabel={cfg.contextLabel}
        contextMeta={cfg.contextMeta}
        triggeredFrom={cfg.triggeredFrom}
        initialTemplateKey={cfg.initialTemplateKey}
        supabase={supabase}
        userId={user?.id}
        leadId={cfg.leadId}
        eventId={cfg.eventId}
        eventName={cfg.eventName}
        onHandoff={cfg.onHandoff}
        onToast={effectiveToast}
      />
      {/* Built-in toast pill (only used if caller didn't pass onToast) */}
      {!onToast && toast && (
        <div
          role="status"
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--ink, #1A1D23)', color: 'var(--paper, #F4EFE6)',
            padding: '10px 18px', borderRadius: 999,
            fontSize: 13, fontWeight: 500, letterSpacing: '0.01em',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            zIndex: 9999, pointerEvents: 'none',
          }}
        >
          {toast}
        </div>
      )}
    </QuickEmailContext.Provider>
  )
}

export default QuickEmailProvider
